import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Text } from "src/components/ui/Text";
import { IconProperties } from "./Icon/index";
import { BaseButton, IconButton } from "src/components/ui/baseButton";
import { Dialog, Zoom } from "@mui/material";
import { getUnitsSiteMapDapById } from "src/api/site";
import TableListUnits from "./TableListUnits";

const DialogDetailBoxType4 = (props) => {
  const { open, onClose, detailSite } = props;
  const { lang } = useParams();
  const [activeTab, setActiveTab] = useState("details");
  const [getUnitsById, setGetUnitsById] = useState([]);

  const basicInformation = detailSite?.basic_info;
  const location = JSON.parse(detailSite?.location);
  const areaMeasurements = detailSite?.area_measurements;
  const financialInformation = detailSite?.financial_info;
  const importDate = detailSite?.import_date;
  const operationalStatus = detailSite?.operational_status;

  const getSiteName = detailSite?.Name.includes('"name_english"')
    ? JSON.parse(detailSite?.Name)
    : detailSite?.Name;

  const siteName =
    typeof getSiteName === "string"
      ? getSiteName
      : lang === "th"
      ? getSiteName?.name_thai
      : getSiteName?.name_english;

  const detailGroup = {
    basicInformation: [
      { name: "Asset Code", value: basicInformation?.asset_code },
      { name: "Asset Parent", value: basicInformation?.asset_parent },
      { name: "Business Unit", value: basicInformation?.business_unit },
      { name: "Asset Class", value: basicInformation?.asset_class },
      { name: "Property Type", value: basicInformation?.property_type },
    ],
    location: [
      { name: "State", value: location?.state },
      { name: "Country", value: location?.country },
      { name: "Latitude", value: location?.lat },
      { name: "Longitude", value: location?.lon },
      { name: "Plots", value: location?.plots },
    ],
    areaMeasurements: [
      { name: "Land Plot Size", value: areaMeasurements?.land_plot_size },
      { name: "Sale Area", value: areaMeasurements?.sale_area },
      { name: "Usable Area", value: areaMeasurements?.usable_area },
      { name: "GFA", value: areaMeasurements?.gfa },
      { name: "NLA", value: areaMeasurements?.nla },
    ],
    financialInformation: [
      {
        name: "Gross Asset Value",
        value: financialInformation?.gross_asset_value,
      },
      {
        name: "Financial Control",
        value: financialInformation?.financial_control,
      },
      {
        name: "Financial Interest",
        value: financialInformation?.financial_interest,
      },
      { name: "Lease Type", value: financialInformation?.lease_type },
      { name: "Usage Type", value: financialInformation?.usage_type },
    ],
    importDate: [
      { name: "Construction Start", value: importDate?.construction_start },
      {
        name: "Construction Complete",
        value: importDate?.construction_complete,
      },
      { name: "Acquisition Date", value: importDate?.acquisition_date },
      { name: "Last Unit Sold", value: importDate?.last_unit_sold },
      { name: "Divestment Date", value: importDate?.divestment_date },
    ],
    operationalStatus: [
      { name: "Property Status", value: operationalStatus?.property_status },
      {
        name: "Operational Control",
        value: operationalStatus?.operational_control,
      },
      { name: "Occ. Rate% FY24", value: operationalStatus?.occ_rate_fy24 },
      { name: "Occ. Rate% FY25", value: operationalStatus?.occ_rate_fy25 },
      { name: "Common Area", value: operationalStatus?.common_area },
      { name: "Certificate", value: operationalStatus?.certificate },
    ],
  };

  async function onGetUnitsById() {
    const response = await getUnitsSiteMapDapById({
      id: detailSite?.Id,
      companyId: detailSite?.companyId,
      branchId: Number(detailSite?.plantId),
      page: 1,
      rowPerPage: 20,
    });

    if (response?.status) {
      setGetUnitsById(response?.data);
    }
  }

  useEffect(() => {
    const init = async () => {
      await onGetUnitsById();
    };
    init();
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionComponent={Zoom}
      sx={{
        "& .MuiDialog-paper": {
          overflow: "hidden",
          maxHeight: "800px",
          height: "75vh",
          borderRadius: "8px",
        },
      }}
    >
      <div className="relative p-6 w-full h-full">
        {/* header */}
        <div className="flex items-center gap-2 pb-4 border-b border-gray-200">
          <IconProperties className="w-5 h-5 stroke-vekin" />
          <Text variant="bold-16" color="black">
            {siteName}
          </Text>
        </div>

        {/* tab active */}
        <div className="w-full flex items-center gap-2 border border-gray-200 rounded-lg p-1 my-4">
          <BaseButton
            variant={activeTab === "details" ? "primary" : "tertiary"}
            size="normal"
            onClick={() => setActiveTab("details")}
            className={`grow !rounded-lg !shadow-none`}
          >
            <Text variant="normal-14">Details</Text>
          </BaseButton>

          <BaseButton
            variant={activeTab === "units" ? "primary" : "tertiary"}
            size="normal"
            onClick={() => setActiveTab("units")}
            className={`grow !rounded-lg !shadow-none`}
          >
            <Text variant="normal-14">Units</Text>
          </BaseButton>
        </div>

        {/* content details*/}
        {activeTab === "details" && (
          <div className="grid grid-cols-2 gap-4 pr-2 pb-2 h-[calc(100%-116px)] overflow-y-auto">
            <ItemInfo
              title="Basic Information"
              value={detailGroup.basicInformation}
            />
            <ItemInfo title="Location" value={detailGroup.location} />
            <ItemInfo
              title="Area Measurements"
              value={detailGroup.areaMeasurements}
              unit="sqm"
            />
            <ItemInfo
              title="Financial Information"
              value={detailGroup.financialInformation}
              unit={financialInformation?.gross_asset_currency || "THB"}
            />
            <ItemInfo title="Important Dates" value={detailGroup.importDate} />
            <ItemInfo
              title="Operational Status"
              value={detailGroup.operationalStatus}
            />
          </div>
        )}

        {/* content units */}
        {activeTab === "units" && (
          <div className=" flex flex-col gap-4 pr-2 pb-2 h-[calc(100%-116px)] overflow-y-auto">
            <div>
              <Text variant="semi-16" className={`text-vekin`}>
                Units in this Building
              </Text>
            </div>

            {/* list units */}
            <div>
              <TableListUnits dataUnitsById={getUnitsById || []} />
            </div>
          </div>
        )}

        {/* close button */}
        <IconButton
          className={`flex items-center !rounded-md !shadow-none absolute top-5 right-5`}
          onClick={onClose}
        >
          <i className="ri-close-line text-xl"></i>
        </IconButton>
      </div>
    </Dialog>
  );
};

export default DialogDetailBoxType4;

const ItemInfo = (props) => {
  const { title, value, unit } = props;

  return (
    <div className="flex flex-col gap-2">
      <Text variant="bold-16" className={`text-vekin`}>
        {title}
      </Text>

      <div className="flex flex-col gap-3 pt-2">
        {value.map((item, index) => {
          const isTitleFinancialInfo = title === "Financial Information";
          const isAreaMeasurements = title === "Area Measurements";
          const isGrossAssetValue = item?.name === "Gross Asset Value";

          const isShowUnit =
            (isTitleFinancialInfo && isGrossAssetValue) || isAreaMeasurements;

          return (
            <div
              key={`${item?.name}-${index}`}
              className="flex items-center justify-between gap-2"
            >
              <Text variant="normal-14" className={`text-vekin/70`}>
                {item?.name}:
              </Text>
              <Text variant="normal-14" className={`text-secondary`}>
                {`${item?.value ?? "-"} ${isShowUnit && unit ? unit : ""}`}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
};
